import React, { useEffect, useState, useContext } from "react";
import { AuthContext } from "../../../AuthContext";
import { FaTrash } from "react-icons/fa";
import { Button, Form, Row, Col, Card, Alert, Spinner } from "react-bootstrap";

const AccionistasTable = ({ idFormulario, isReadOnly, onTotalChange }) => {
  const [accionistas, setAccionistas] = useState([]);
  const [nombre, setNombre] = useState("");
  const [cedula, setCedula] = useState("");
  const [porcentaje, setPorcentaje] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [cedulaError, setCedulaError] = useState("");
  const [nombreError, setNombreError] = useState("");
  const [primerAccionistaError, setPrimerAccionistaError] = useState("");

  const { verifySession } = useContext(AuthContext);

  const validarNombre = (nombre) => {
    if (!nombre.trim()) return "El nombre es requerido";
    if (nombre.length > 100) return "El nombre no puede exceder 100 caracteres";
    return "";
  };

  const validarRUC = (ruc) => {
    if (!ruc) return "El RUC es requerido";
    if (!/^\d+$/.test(ruc)) return "El RUC solo debe contener números";
    if (ruc.length !== 13 && ruc.length !== 10) return "El número debe tener 10 o 13 dígitos";
    return "";
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return {
      "Content-Type": "application/json",
      "Authorization": token ? `Bearer ${token}` : ""
    };
  };

  const load = async () => {
    if (!idFormulario) return;
    setIsLoading(true);
    setError("");
    try {
      const isSessionValid = await verifySession();
      if (!isSessionValid) {
        setError("La sesión ha expirado. Por favor, recargue la página.");
        setIsLoading(false);
        return;
      }
      const res = await fetch(
        `${process.env.REACT_APP_API_URL || 'https://apiprov.conduto.com.ec'}/api/providers/accionistas/${idFormulario}`,
        { headers: getAuthHeaders(), credentials: "include" }
      );
      if (res.status === 401) {
        const refreshed = await verifySession();
        if (refreshed) {
          const retryRes = await fetch(
            `${process.env.REACT_APP_API_URL || 'https://apiprov.conduto.com.ec'}/api/providers/accionistas/${idFormulario}`,
            { headers: getAuthHeaders(), credentials: "include" }
          );
          if (retryRes.ok) {
            const data = await retryRes.json();
            const arr = Array.isArray(data) ? data : data.accionistas || [];
            setAccionistas(arr);
            if (onTotalChange) onTotalChange(arr.reduce((s, a) => s + a.porcentaje, 0));
          } else {
            setError("Error al cargar accionistas");
          }
        } else {
          setError("No autorizado. La sesión ha expirado.");
        }
        setIsLoading(false);
        return;
      }
      if (res.ok) {
        const data = await res.json();
        const arr = Array.isArray(data) ? data : data.accionistas || [];
        setAccionistas(arr);
        if (onTotalChange) onTotalChange(arr.reduce((s, a) => s + a.porcentaje, 0));
      } else {
        setError("Error al cargar accionistas");
      }
    } catch {
      setError("Error de conexión al cargar accionistas");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (idFormulario) {
      const timer = setTimeout(() => load(), 200);
      return () => clearTimeout(timer);
    }
  }, [idFormulario]);

  // Validar si se puede agregar el primer accionista
  const validarPrimerAccionista = (porcentajeValue) => {
    if (accionistas.length === 0) {
      const porcentajeNum = parseFloat(porcentajeValue);
      if (porcentajeNum < 25) {
        return "El primer accionista debe tener al menos 25% de participación";
      }
    }
    return "";
  };

  const add = async () => {
    setError("");
    setCedulaError("");
    setNombreError("");
    setPrimerAccionistaError("");

    const nErr = validarNombre(nombre.trim());
    if (nErr) { setNombreError(nErr); return; }

    const rErr = validarRUC(cedula.trim());
    if (rErr) { setCedulaError(rErr); return; }

    const nuevoPorcentaje = parseFloat(porcentaje);
    const totalActual = accionistas.reduce((s, a) => s + a.porcentaje, 0);

    // Validar primer accionista
    const primerErr = validarPrimerAccionista(porcentaje);
    if (primerErr) {
      setPrimerAccionistaError(primerErr);
      return;
    }

    if (nuevoPorcentaje <= 0) { setError("El porcentaje debe ser mayor a 0"); return; }
    if (totalActual + nuevoPorcentaje > 100) { setError("La suma total no puede exceder el 100%"); return; }

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL || 'https://apiprov.conduto.com.ec'}/api/providers/accionistas/${idFormulario}`,
        {
          method: "POST",
          headers: getAuthHeaders(),
          body: JSON.stringify({ nombre: nombre.trim(), cedula: cedula.trim(), porcentaje: nuevoPorcentaje }),
          credentials: "include"
        }
      );
      if (res.ok) {
        await load();
        setNombre(""); setCedula(""); setPorcentaje("");
        setPrimerAccionistaError("");
      } else {
        const data = await res.json();
        setError(data.detail || "Error al agregar accionista");
      }
    } catch {
      setError("Error de conexión");
    }
  };

  const remove = async (id) => {
    try {
      await fetch(
        `${process.env.REACT_APP_API_URL || 'https://apiprov.conduto.com.ec'}/api/providers/accionistas/${id}`,
        { method: "DELETE", headers: getAuthHeaders(), credentials: "include" }
      );
      await load();
    } catch {
      setError("Error de conexión al eliminar accionista");
    }
  };

  const handleCedulaChange = (e) => {
    let value = e.target.value.replace(/[^0-9]/g, '').slice(0, 13);
    setCedula(value);
    if (value) setCedulaError(validarRUC(value));
    else setCedulaError("");
  };

  const handleNombreChange = (e) => {
    const value = e.target.value;
    setNombre(value);
    if (value.trim()) setNombreError(validarNombre(value.trim()));
    else setNombreError("");
  };

  const handlePorcentajeChange = (e) => {
    const value = e.target.value;
    setPorcentaje(value);

    // Validar primer accionista en tiempo real
    if (accionistas.length === 0 && value) {
      const porcentajeNum = parseFloat(value);
      if (porcentajeNum < 25 && porcentajeNum > 0) {
        setPrimerAccionistaError("El primer accionista debe tener al menos 25% de participación");
      } else if (porcentajeNum >= 25 && porcentajeNum <= 100) {
        setPrimerAccionistaError("");
      } else if (porcentajeNum > 100) {
        setPrimerAccionistaError("El porcentaje no puede exceder 100%");
      } else {
        setPrimerAccionistaError("");
      }
    } else if (accionistas.length > 0) {
      setPrimerAccionistaError("");
    }
  };

  const total = accionistas.reduce((s, a) => s + a.porcentaje, 0);
  const isValid = total >= 25 && total <= 100;
  const hasExceeded = total > 100;

  // Verificar si el botón de agregar debe estar deshabilitado
  const isAddButtonDisabled = () => {
    if (!nombre.trim() || !cedula.trim() || !porcentaje || !!cedulaError || !!nombreError) {
      return true;
    }

    // Para el primer accionista, validar que tenga al menos 25%
    if (accionistas.length === 0) {
      const porcentajeNum = parseFloat(porcentaje);
      if (isNaN(porcentajeNum) || porcentajeNum < 25) {
        return true;
      }
    }

    return false;
  };

  useEffect(() => {
    if (onTotalChange) onTotalChange(total);
  }, [total, onTotalChange]);

  if (isLoading) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" variant="primary" size="sm" />
        <p className="mt-2 small">Cargando accionistas...</p>
      </div>
    );
  }

  return (
    <div>
      <Form.Label className="required-field fw-semibold">ACCIONISTAS</Form.Label>
      <div className="mb-2">
        <span className="text-muted small">Ingrese primero el accionista con mayor participación (superior o igual al 25%)</span>
      </div>

      <div className="mb-3">
        {accionistas.length === 0 ? (
          <div className="text-center text-muted py-3 small border rounded bg-light">
            No hay accionistas agregados.
          </div>
        ) : (
          <div className="d-flex flex-column gap-2">

            <div className="row fw-semibold text-muted small px-2 d-none d-sm-flex">
              <div className="col-sm-7">Accionista</div>
              <div className="col-sm-3">Cédula / RUC</div>
              <div className="col-sm-1">%</div>
              {!isReadOnly && (
                <div className="col-sm-1 text-end">Acción</div>
              )}
            </div>

            {accionistas.map((a, index) => (
              <div
                key={a.id}
                className={`border rounded bg-white shadow-sm p-2 ${index === 0 ? 'border-primary' : ''}`}
              >
                <div className="row align-items-center g-2">
                  <div className="col-12 col-sm-7">
                    <small className="text-muted d-block d-sm-none">Accionista</small>
                    <div
                      className="small text-truncate"
                      style={{
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                      }}
                    >
                      {a.nombre}
                    </div>
                  </div>

                  <div className="col-12 col-sm-3">
                    <small className="text-muted d-block d-sm-none">Cédula / RUC</small>
                    <div className="small text-truncate">
                      {a.cedula}
                    </div>
                  </div>

                  <div className="col-6 col-sm-1">
                    <small className="text-muted d-block d-sm-none">%</small>
                    <span className={`badge w-100 ${index === 0 ? 'bg-primary' : 'bg-secondary'}`}>
                      {a.porcentaje}%
                    </span>
                  </div>

                  {!isReadOnly && (
                    <div className="col-6 col-sm-1 d-flex justify-content-end">
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => remove(a.id)}
                        title="Eliminar"
                        className="p-1 d-flex align-items-center justify-content-center"
                        style={{ width: "32px", height: "32px" }}
                      >
                        <FaTrash size={12} />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}

          </div>
        )}
      </div>

      {!isReadOnly && (
        <Card className="bg-light border-0 mb-3">
          <Card.Body className="p-2 p-sm-3">
            <Row className="g-2">
              <Col xs={12} sm={6} md={5}>
                <Form.Group>
                  <Form.Label className="small fw-semibold mb-1">Accionista</Form.Label>
                  <Form.Control
                    maxLength="100" size="sm" isInvalid={!!nombreError}
                    placeholder="Nombre completo" value={nombre} onChange={handleNombreChange}
                  />
                  {nombreError && <Form.Text className="text-danger small">{nombreError}</Form.Text>}
                </Form.Group>
              </Col>
              <Col xs={12} sm={6} md={3}>
                <Form.Group>
                  <Form.Label className="small fw-semibold mb-1">Cédula/RUC</Form.Label>
                  <Form.Control
                    type="text" inputMode="numeric" size="sm" isInvalid={!!cedulaError}
                    value={cedula} onChange={handleCedulaChange} maxLength={13}
                  />
                  {cedulaError && <Form.Text className="text-danger small">{cedulaError}</Form.Text>}
                </Form.Group>
              </Col>
              <Col xs={6} sm={4} md={2}>
                <Form.Group>
                  <Form.Label className="small fw-semibold mb-1">Porcentaje %</Form.Label>
                  <Form.Control
                    size="sm"
                    type="number"
                    placeholder="0.00"
                    min="0.01"
                    max="100"
                    step="0.01"
                    value={porcentaje}
                    onChange={handlePorcentajeChange}
                    isInvalid={!!primerAccionistaError}
                  />
                  {primerAccionistaError && (
                    <Form.Text className="text-danger small">{primerAccionistaError}</Form.Text>
                  )}
                </Form.Group>
              </Col>
              <Col xs={6} sm={2} md={2} className="d-flex align-items-end">
                <Button
                  variant="success"
                  size="sm"
                  className="w-100"
                  onClick={add}
                  disabled={isAddButtonDisabled()}
                >
                  <i className="bi bi-plus-circle me-1"></i>Agregar
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      <div className={`p-2 p-sm-3 rounded ${hasExceeded ? 'bg-danger bg-opacity-10' : isValid ? 'bg-success bg-opacity-10' : 'bg-warning bg-opacity-10'}`}>
        <Row className="align-items-center g-2">
          <Col xs={12} sm={6}>
            <div className="d-flex align-items-center gap-2">
              <i className={`bi ${hasExceeded ? 'bi-exclamation-triangle-fill text-danger' : isValid ? 'bi-check-circle-fill text-success' : 'bi-info-circle-fill text-warning'} fs-5`}></i>
              <div>
                <div className="fw-semibold small">Resumen de Participación</div>
                <div className="small">Total: <strong>{total.toFixed(2)}%</strong></div>
              </div>
            </div>
          </Col>
          <Col xs={12} sm={6}>
            <div className="progress mb-1" style={{ height: '6px' }}>
              <div
                className={`progress-bar ${hasExceeded ? 'bg-danger' : isValid ? 'bg-success' : 'bg-warning'}`}
                style={{ width: `${Math.min(total, 100)}%` }}
              ></div>
            </div>
            <div className="small">
              {hasExceeded
                ? <span className="text-danger">Excede el límite del 100%</span>
                : isValid
                  ? <span className="text-success">Cumple con el mínimo (25%)</span>
                  : <span className="text-secondary">Requiere al menos 25%</span>
              }
            </div>
          </Col>
        </Row>
      </div>

      {error && (
        <Alert variant="danger" className="mt-3 py-2 small" dismissible onClose={() => setError("")}>
          {error}
        </Alert>
      )}
    </div>
  );
};

export default AccionistasTable;